import { lazy, Suspense, type ComponentType } from 'react'
import { createBrowserRouter, Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom'
import { AuthGuard, GuestGuard, OnboardingGuard, TesterGuard } from './guards'

// Layout (keep eagerly loaded – they wrap every page)
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout'
import { AdminLayout } from '@/components/admin'
import { ScrollToTop } from '@/components/layout/ScrollToTop'
import { SmartAppBanner } from '@/components/layout/SmartAppBanner'

// ---------------------------------------------------------------------------
// Lazy-loaded pages — each goes into its own chunk
// ---------------------------------------------------------------------------

/** Helper: wraps a named-export page module for React.lazy (which needs a default export). */
function lazyNamed<T extends Record<string, ComponentType>>(
  factory: () => Promise<T>,
  name: keyof T,
) {
  return lazy(() => factory().then((m) => ({ default: m[name] as ComponentType })))
}

// Auth / public
const AuthCallbackPage = lazyNamed(() => import('@/pages/AuthCallbackPage'), 'AuthCallbackPage')
const LoginPage = lazyNamed(() => import('@/pages/LoginPage'), 'LoginPage')
const PrivacyPolicyPage = lazyNamed(() => import('@/pages/PrivacyPolicyPage'), 'PrivacyPolicyPage')
const TermsOfServicePage = lazyNamed(() => import('@/pages/TermsOfServicePage'), 'TermsOfServicePage')
const SupportPage = lazyNamed(() => import('@/pages/SupportPage'), 'SupportPage')

// Core app
const WelcomePage = lazyNamed(() => import('@/pages/WelcomePage'), 'WelcomePage')
const DashboardPage = lazyNamed(() => import('@/pages/DashboardPage'), 'DashboardPage')
const ProfilePage = lazyNamed(() => import('@/pages/ProfilePage'), 'ProfilePage')
const PublicProfilePage = lazyNamed(() => import('@/pages/PublicProfilePage'), 'PublicProfilePage')
const SettingsPage = lazyNamed(() => import('@/pages/SettingsPage'), 'SettingsPage')
const ChallengesPage = lazyNamed(() => import('@/pages/ChallengesPage'), 'ChallengesPage')
const CommunityPage = lazyNamed(() => import('@/pages/CommunityPage'), 'CommunityPage')
const PartnerDashboardPage = lazyNamed(() => import('@/pages/PartnerDashboardPage'), 'PartnerDashboardPage')
const ShopPage = lazyNamed(() => import('@/pages/ShopPage'), 'ShopPage')
const PomodoroPage = lazyNamed(() => import('@/pages/PomodoroPage'), 'PomodoroPage')

// Dev / test
const TestPage = lazyNamed(() => import('@/pages/TestPage'), 'TestPage')
const AnimationTestPage = lazyNamed(() => import('@/pages/AnimationTestPage'), 'AnimationTestPage')

// Admin (all behind admin layout, rarely visited)
const AdminOverviewPage = lazyNamed(() => import('@/pages/admin/AdminOverviewPage'), 'AdminOverviewPage')
const AdminUsersPage = lazyNamed(() => import('@/pages/admin/AdminUsersPage'), 'AdminUsersPage')
const AdminAnalyticsPage = lazyNamed(() => import('@/pages/admin/AdminAnalyticsPage'), 'AdminAnalyticsPage')
const AdminEngagementPage = lazyNamed(() => import('@/pages/admin/AdminEngagementPage'), 'AdminEngagementPage')
const AdminEconomyPage = lazyNamed(() => import('@/pages/admin/AdminEconomyPage'), 'AdminEconomyPage')
const AdminSubscriptionsPage = lazyNamed(() => import('@/pages/admin/AdminSubscriptionsPage'), 'AdminSubscriptionsPage')
const AdminNotificationsPage = lazyNamed(() => import('@/pages/admin/AdminNotificationsPage'), 'AdminNotificationsPage')
const AdminPushPage = lazyNamed(() => import('@/pages/admin/AdminPushPage'), 'AdminPushPage')
const AdminChallengesPage = lazyNamed(() => import('@/pages/admin/AdminChallengesPage'), 'AdminChallengesPage')
const AdminBadgesPage = lazyNamed(() => import('@/pages/admin/AdminBadgesPage'), 'AdminBadgesPage')
const AdminBugsPage = lazyNamed(() => import('@/pages/admin/AdminBugsPage'), 'AdminBugsPage')
const SubscriptionSuccessPage = lazyNamed(() => import('@/pages/SubscriptionSuccessPage'), 'SubscriptionSuccessPage')
const AdminLightsPage = lazyNamed(() => import('@/pages/admin/AdminLightsPage'), 'AdminLightsPage')
const AdminBIPage = lazyNamed(() => import('@/pages/admin/AdminBIPage'), 'AdminBIPage')
const AdminPulsePage = lazyNamed(() => import('@/pages/admin/AdminPulsePage'), 'AdminPulsePage')

// Redirects /habit/:id?foo=bar → /?habit=:id&foo=bar (opens drawer on dashboard)
function HabitDetailRedirect() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const params = new URLSearchParams(searchParams)
  if (id) params.set('habit', id)
  return <Navigate to={`/?${params.toString()}`} replace />
}

// Redirects /habit/:id/edit → /?editHabit=:id (opens edit drawer on dashboard)
function EditHabitRedirect() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const params = new URLSearchParams(searchParams)
  if (id) params.set('editHabit', id)
  return <Navigate to={`/?${params.toString()}`} replace />
}

// Minimal suspense fallback (invisible – the page shell/layout is already rendered)
function PageSuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div className="min-h-screen" />}>{children}</Suspense>
}

// Root layout component (minimal wrapper)
function RootLayout() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <ScrollToTop />
      <SmartAppBanner />
      <Outlet />
    </div>
  )
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Auth callback route (handles OAuth redirect)
      {
        path: '/auth/callback',
        element: <PageSuspense><AuthCallbackPage /></PageSuspense>,
      },
      
      // Test page (always accessible)
      {
        path: '/test',
        element: <PageSuspense><TestPage /></PageSuspense>,
      },
      
      // Legal pages (public, no auth required)
      {
        path: '/privacy',
        element: <PageSuspense><PrivacyPolicyPage /></PageSuspense>,
      },
      {
        path: '/terms',
        element: <PageSuspense><TermsOfServicePage /></PageSuspense>,
      },
      {
        path: '/support',
        element: <PageSuspense><SupportPage /></PageSuspense>,
      },
      {
        path: '/subscription-success',
        element: <PageSuspense><SubscriptionSuccessPage /></PageSuspense>,
      },
      
      // Animation testing page (tester only)
      {
        element: <TesterGuard />,
        children: [
          {
            path: '/test-animations',
            element: <PageSuspense><AnimationTestPage /></PageSuspense>,
          },
        ],
      },
      
      // Guest-only routes (redirect to home if logged in)
      {
        element: <GuestGuard />,
        children: [
          {
            path: '/login',
            element: <PageSuspense><LoginPage /></PageSuspense>,
          },
        ],
      },
      
      // Welcome page (protected but full-screen, no layout)
      {
        element: <AuthGuard />,
        children: [
          {
            path: '/welcome',
            element: <PageSuspense><WelcomePage /></PageSuspense>,
          },
        ],
      },
      
      // Protected routes with app layout (require authentication + onboarding)
      {
        element: <OnboardingGuard />,
        children: [
          {
            element: <AuthenticatedLayout />,
            children: [
              {
                path: '/pomodoro',
                element: <PageSuspense><PomodoroPage /></PageSuspense>,
              },
              {
                path: '/',
                element: <PageSuspense><DashboardPage /></PageSuspense>,
              },
              {
                path: '/habit/:id',
                element: <HabitDetailRedirect />,
              },
              {
                path: '/create-habit',
                element: <Navigate to="/?createHabit=true" replace />,
              },
              {
                path: '/challenges',
                element: <PageSuspense><ChallengesPage /></PageSuspense>,
              },
              {
                path: '/community',
                element: <PageSuspense><CommunityPage /></PageSuspense>,
              },
              {
                path: '/social',
                element: <Navigate to="/community?tab=people" replace />,
              },
              {
                path: '/partner/:id',
                element: <PageSuspense><PartnerDashboardPage /></PageSuspense>,
              },
              {
                path: '/shop',
                element: <PageSuspense><ShopPage /></PageSuspense>,
              },
              {
                path: '/habit/:id/edit',
                element: <EditHabitRedirect />,
              },
              {
                path: '/settings',
                element: <PageSuspense><SettingsPage /></PageSuspense>,
              },
              {
                path: '/profile',
                element: <PageSuspense><ProfilePage /></PageSuspense>,
              },
              {
                path: '/badges',
                element: <Navigate to="/profile" replace />,
              },
              {
                path: '/u/:username',
                element: <PageSuspense><PublicProfilePage /></PageSuspense>,
              },
            ],
          },
        ],
      },
      
      // Admin routes (desktop only, protected)
      {
        element: <AdminLayout />,
        children: [
          {
            path: '/admin',
            element: <PageSuspense><AdminOverviewPage /></PageSuspense>,
          },
          {
            path: '/admin/users',
            element: <PageSuspense><AdminUsersPage /></PageSuspense>,
          },
          {
            path: '/admin/analytics',
            element: <PageSuspense><AdminAnalyticsPage /></PageSuspense>,
          },
          {
            path: '/admin/engagement',
            element: <PageSuspense><AdminEngagementPage /></PageSuspense>,
          },
          {
            path: '/admin/economy',
            element: <PageSuspense><AdminEconomyPage /></PageSuspense>,
          },
          {
            path: '/admin/subscriptions',
            element: <PageSuspense><AdminSubscriptionsPage /></PageSuspense>,
          },
          {
            path: '/admin/notifications',
            element: <PageSuspense><AdminNotificationsPage /></PageSuspense>,
          },
          {
            path: '/admin/push',
            element: <PageSuspense><AdminPushPage /></PageSuspense>,
          },
          {
            path: '/admin/challenges',
            element: <PageSuspense><AdminChallengesPage /></PageSuspense>,
          },
          {
            path: '/admin/badges',
            element: <PageSuspense><AdminBadgesPage /></PageSuspense>,
          },
          {
            path: '/admin/bi',
            element: <PageSuspense><AdminBIPage /></PageSuspense>,
          },
          {
            path: '/admin/bugs',
            element: <PageSuspense><AdminBugsPage /></PageSuspense>,
          },
          {
            path: '/admin/lights',
            element: <PageSuspense><AdminLightsPage /></PageSuspense>,
          },
          {
            path: '/admin/pulse',
            element: <PageSuspense><AdminPulsePage /></PageSuspense>,
          },
        ],
      },
      
      // Catch all - redirect to home
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
])

